import React from "react";
import { motion } from "framer-motion";
import { FaWhatsapp, FaInstagram, FaCrown } from "react-icons/fa";
import { IoGameController } from "react-icons/io5";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 280, damping: 26 },
  },
};

const KwaiIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2.2 14.3-3.1-3.1V8.8h2.4v3.5l3.5-3.5h3.1l-4 4 4.2 4.5h-3.2l-2.9-3.2v3.2H9.8z" />
  </svg>
);

const SecondaryLink = ({
  href,
  testId,
  icon: Icon,
  label,
  accentClass,
  iconBg,
}) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    variants={itemVariants}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`group relative flex w-full items-center rounded-2xl border ${accentClass} backdrop-blur-xl py-4 px-4 transition-colors`}
  >
    <span
      className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg} shrink-0`}
    >
      <Icon className="text-xl" />
    </span>
    <span className="flex-1 text-center font-semibold tracking-wide text-[15px] sm:text-base pr-10">
      {label}
    </span>
  </motion.a>
);

export default function LinkTree() {
  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white">
      {/* Animated mesh background */}
      <div className="bg-mesh" aria-hidden />
      <div className="bg-grid" aria-hidden />
      <div className="orb orb-1" aria-hidden />
      <div className="orb orb-2" aria-hidden />

      {/* Diagonal light beams */}
      <div className="bg-beam b1" aria-hidden />
      <div className="bg-beam b2" aria-hidden />
      <div className="bg-beam b3" aria-hidden />

      {/* Twinkling stars */}
      <span className="bg-star t1" aria-hidden />
      <span className="bg-star t2" aria-hidden />
      <span className="bg-star t3" aria-hidden />
      <span className="bg-star t4" aria-hidden />
      <span className="bg-star t5" aria-hidden />
      <span className="bg-star t6" aria-hidden />
      <span className="bg-star t7" aria-hidden />
      <span className="bg-star t8" aria-hidden />
      <span className="bg-star t9" aria-hidden />
      <span className="bg-star t10" aria-hidden />

      {/* Rising gold particles */}
      <span className="bg-particle p1" aria-hidden />
      <span className="bg-particle p2" aria-hidden />
      <span className="bg-particle p3" aria-hidden />
      <span className="bg-particle p4" aria-hidden />
      <span className="bg-particle p5" aria-hidden />
      <span className="bg-particle p6" aria-hidden />
      <span className="bg-particle p7" aria-hidden />
      <span className="bg-particle p8" aria-hidden />
      <span className="bg-particle p9" aria-hidden />
      <span className="bg-particle p10" aria-hidden />
      <span className="bg-particle p11" aria-hidden />
      <span className="bg-particle p12" aria-hidden />
      {/* Vignette for legibility */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)",
        }}
        aria-hidden
      />
      {/* Grain */}
      <div className="grain" aria-hidden />

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col px-5 py-10 sm:py-14">
        {/* Hero — Welcome logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22 }}
          className="relative flex flex-col items-center text-center py-8"
          data-testid="hero-welcome"
        >
          {/* Decorative background layers */}
          <div className="hero-rays" aria-hidden />
          <div className="hero-aura" aria-hidden />

          {/* Floating sparkles */}
          <span className="sparkle s1" aria-hidden />
          <span className="sparkle s2" aria-hidden />
          <span className="sparkle s3" aria-hidden />
          <span className="sparkle s4" aria-hidden />
          <span className="sparkle s5" aria-hidden />
          <span className="sparkle s6" aria-hidden />

          {/* Crown */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 14 }}
            className="crown-bounce mb-2"
          >
            <FaCrown className="text-3xl text-[#FFD700]" />
          </motion.div>

          {/* Hero with side ornaments */}
          <div className="flex items-center gap-3 w-full">
            <motion.div
              className="hero-ornament"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              style={{ transformOrigin: "right" }}
              aria-hidden
            />

            {/* Title — letter by letter */}
            <h1
              className="hero-title text-[2.25rem] sm:text-5xl leading-none whitespace-nowrap"
              data-testid="hero-title"
              aria-label="Bem-vindos"
            >
              {"BEM-VINDOS".split("").map((char, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, rotateX: -90 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{
                    delay: 0.3 + i * 0.07,
                    type: "spring",
                    stiffness: 260,
                    damping: 18,
                  }}
                  style={{ display: "inline-block" }}
                >
                  {char}
                </motion.span>
              ))}
            </h1>

            <motion.div
              className="hero-ornament"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              style={{ transformOrigin: "left" }}
              aria-hidden
            />
          </div>

          {/* Subtitle ornament — small diamond divider */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="mt-4 flex items-center gap-3"
          >
            <span className="block h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs">◆</span>
            <span className="block h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </motion.div>
        </motion.div>

        {/* Buttons stack */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-col gap-4"
        >
          {/* === MAIN HIGHLIGHT: Plataforma XG (BLACK & GOLD) === */}
          <motion.a
            href="https://xgjogo2.com/register/?ch=190001&ic=1166108"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-platform-xg"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="vg-pulse shine relative flex w-full items-center justify-center overflow-hidden rounded-2xl px-5 py-6"
            style={{
              background:
                "linear-gradient(135deg, #0a0a0a 0%, #1a1410 50%, #0a0a0a 100%)",
              border: "1px solid rgba(212, 175, 55, 0.5)",
              color: "#FFE17A",
            }}
          >
            <div
              className="absolute left-5 flex h-12 w-12 items-center justify-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                boxShadow: "0 0 18px rgba(212, 175, 55, 0.4)",
              }}
            >
              <IoGameController className="text-3xl text-black" />
            </div>
            <div className="flex flex-col items-center px-12">
              <span
                className="font-black text-2xl leading-tight tracking-tight"
                style={{
                  background:
                    "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 40%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                PLATAFORMA XG
              </span>
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#D4AF37]/70">
                Link oficial
              </span>
            </div>
          </motion.a>

          {/* === MAIN HIGHLIGHT: Plataforma VG (BLACK & GOLD) === */}
          <motion.a
            href="https://vgjogo3.com/?ch=160001&ic=1238831"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-platform-vg"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="vg-pulse shine relative flex w-full items-center justify-center overflow-hidden rounded-2xl px-5 py-6"
            style={{
              background:
                "linear-gradient(135deg, #0a0a0a 0%, #1a1410 50%, #0a0a0a 100%)",
              border: "1px solid rgba(212, 175, 55, 0.5)",
              color: "#FFE17A",
            }}
          >
            <div
              className="absolute left-5 flex h-12 w-12 items-center justify-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                boxShadow: "0 0 18px rgba(212, 175, 55, 0.4)",
              }}
            >
              <IoGameController className="text-3xl text-black" />
            </div>
            <div className="flex flex-col items-center px-12">
              <span
                className="font-black text-2xl leading-tight tracking-tight"
                style={{
                  background:
                    "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 40%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                PLATAFORMA VG
              </span>
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#D4AF37]/70">
                Link oficial
              </span>
            </div>
          </motion.a>

          {/* === Plataforma HG === */}
          <motion.a
            href="https://hgjogo8.com/?ch=110003&ic=139984855#/register"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-platform-hg"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="group relative flex w-full items-center rounded-2xl border border-[#D4AF37]/15 bg-white/[0.03] backdrop-blur-xl py-4 px-4 transition-colors hover:border-[#D4AF37]/40 hover:bg-white/[0.06]"
          >
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl font-black text-sm shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                color: "#0a0a0a",
                boxShadow: "0 0 14px rgba(212, 175, 55, 0.35)",
              }}
            >
              HG
            </span>
            <span className="flex-1 text-center font-semibold tracking-wide text-[15px] sm:text-base pr-10 text-white/90">
              Plataforma HG
            </span>
          </motion.a>

          {/* === WhatsApp === */}
          <SecondaryLink
            href="https://chat.whatsapp.com/IN3gz1oqWio2mHvL0CYJZY"
            testId="link-whatsapp-group"
            icon={FaWhatsapp}
            label="Grupo WhatsApp"
            accentClass="border-[#D4AF37]/20 bg-white/[0.03] text-white/85 hover:border-[#D4AF37]/45 hover:bg-white/[0.06]"
            iconBg="bg-[#25D366] text-black"
          />

          {/* === Kwai === */}
          <SecondaryLink
            href="https://k.kwai.com/u/@VGAndressa/OCHAgBlQ"
            testId="link-kwai"
            icon={KwaiIcon}
            label="Kwai"
            accentClass="border-[#D4AF37]/20 bg-white/[0.03] text-white/85 hover:border-[#D4AF37]/45 hover:bg-white/[0.06]"
            iconBg="bg-[#FF7700] text-white"
          />

          {/* === Instagram === */}
          <SecondaryLink
            href="https://www.instagram.com/andressajogadorakwai?igsh=MWVvdnAwc3lrb3RrZA=="
            testId="link-instagram"
            icon={FaInstagram}
            label="Instagram"
            accentClass="border-[#D4AF37]/20 bg-white/[0.03] text-white/85 hover:border-[#D4AF37]/45 hover:bg-white/[0.06]"
            iconBg="bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white"
          />
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-auto pt-12 text-center"
          data-testid="page-footer"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">
            Todos os direitos reservados
          </p>
        </motion.footer>
      </div>
    </div>
  );
}
